import type { QueryFunctionContext } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import groq from 'groq';
import { PRODUCT_ACCORDION_FIELDS, RICH_TEXT } from 'lib/queries';
import { productHref } from 'lib/queries/product/productHref';
import { client } from 'lib/sanity';
import type { Ingredients, ProductPage } from 'types/cms/pages/ProductPage';
import type { ProductType } from 'types/product/Product';

export const PRODUCT_ACCORDION_CONTENT_QUERY = 'productAccordionContent';

export type ProductAccordionContentQueryKey = [string, string];

export interface ProductAccordionContent {
  title: string;
  productType: ProductType;
  href: string;
  howToUseV2?: ProductPage['howToUseV2'];
  bulletPoints?: ProductPage['bulletPoints'];
  ingredients?: Ingredients;
  informationPanel?: ProductPage['informationPanel'];
}

const fetchProductAccordionContent = async ({
  queryKey,
}: QueryFunctionContext<ProductAccordionContentQueryKey>) => {
  const [, slug] = queryKey;

  const query = groq`
    *[_type == "product" && !(_id in path('drafts.**')) && store.slug.current == $slug][0] {
      title,
      "productType": store.productType,
      ${productHref()},
      "bulletPoints": bulletPointsV2[] { ${RICH_TEXT} },
      ${PRODUCT_ACCORDION_FIELDS},
    }
  `;

  return client.fetch<ProductAccordionContent>(query, { slug });
};

export const useProductAccordionContent = (
  slug: string,
  { enabled }: { enabled: boolean },
) =>
  useQuery({
    queryKey: [PRODUCT_ACCORDION_CONTENT_QUERY, slug],
    queryFn: fetchProductAccordionContent,
    enabled,
  });
